import React, { useState } from "react";

const MAX_IMAGES = 11;

const CaptureUploadImage = () => {
  const [images, setImages] = useState(Array(MAX_IMAGES).fill(null)); // Store 11 images
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [responses, setResponses] = useState(Array(MAX_IMAGES).fill(null)); // Store 11 responses
  const [finalPayload, setFinalPayload] = useState({}); // Accumulate AI responses
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [busyDialog, setBusyDialog] = useState(false);

  // For OpenAI key, use env variable for safety
  const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;

  const handleCapture = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      alert("Image must be less than 20MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const newImages = [...images];
      newImages[currentIndex] = e.target.result;
      setImages(newImages);
    };
    reader.readAsDataURL(file);
  };

  // Utility: Deep merge, only overwrite if new value is not empty/undefined/null
  function deepMergeKeepExisting(oldObj, newObj) {
    if (Array.isArray(oldObj) && Array.isArray(newObj)) {
      // If new array is not empty, use it; else keep old
      return newObj.length > 0 ? newObj : oldObj;
    } else if (
      oldObj &&
      typeof oldObj === "object" &&
      newObj &&
      typeof newObj === "object"
    ) {
      const merged = { ...oldObj };
      for (const key of Object.keys(newObj)) {
        if (
          newObj[key] !== undefined &&
          newObj[key] !== null &&
          (typeof newObj[key] !== "string" || newObj[key].trim() !== "") &&
          (typeof newObj[key] !== "object" || (Array.isArray(newObj[key]) ? newObj[key].length > 0 : Object.keys(newObj[key]).length > 0))
        ) {
          merged[key] =
            key in oldObj
              ? deepMergeKeepExisting(oldObj[key], newObj[key])
              : newObj[key];
        }
        // else: keep old value
      }
      return merged;
    } else {
      // Primitive: if new value is not empty, use it; else keep old
      if (
        newObj !== undefined &&
        newObj !== null &&
        (typeof newObj !== "string" || newObj.trim() !== "") &&
        (typeof newObj !== "object" || (Array.isArray(newObj) ? newObj.length > 0 : Object.keys(newObj).length > 0))
      ) {
        return newObj;
      }
      return oldObj;
    }
  }

  const handleSendToOpenAI = async () => {
    if (!images[currentIndex]) {
      alert("Please capture or select an image first.");
      return;
    }
    setLoading(true);
    setBusyDialog(true);

    try {
      const payloadStructureString = `{
            \"householdType\": \"\",
            \"longitude\": null,
            \"latitude\": null,
            \"houseNo\": \"\",
            \"street\": \"\",
            \"purokSitio\": \"\",
            \"barangay\": \"\",
            \"cityMunicipality\": \"\",
            \"province\": \"\",
            \"municipality\": \"\",
            \"firstName\": \"\",
            \"middleName\": \"\",
            \"lastName\": \"\",
            \"suffix\": \"\",
            \"familyType\": \"\",
            \"headID\": \"\",
            \"status\": \"\",
            \"sex\": \"\",
            \"gender\": \"\",
            \"civilStatus\": \"\",
            \"numFamilyMembers\": null,
            \"yearMoved\": null,
            \"prevLocation\": \"\",
            \"mobileProvider\": [],
            \"contactNumber\": [],
            \"internetProvider\": \"\",
            \"internetProviderOther\": \"\",
            \"householdAppliances\": {},
            \"ictDevices\": {},
            \"vehicles\": {},
            \"householdAssets\": [],
            \"lotOwnership\": \"\",
            \"lotOwnershipType\": \"\",
            \"houseOwnership\": \"\",
            \"houseOwnershipType\": \"\",
            \"lotOwnershipOther\": \"\",
            \"houseOwnershipOther\": \"\",
            \"houseStructure\": \"\",
            \"houseStructureType\": \"\",
            \"houseStructureOther\": \"\",
            \"externalHouseWall\": \"\",
            \"externalHouseWallType\": \"\",
            \"externalHouseWallOther\": \"\",
            \"roofingMat\": \"\",
            \"roofingMatType\": \"\",
            \"roofingMatOther\": \"\",
            \"floorMat\": \"\",
            \"floorMatType\": \"\",
            \"floorMatOther\": \"\",
            \"sourceElect\": \"\",
            \"sourceElectType\": \"\",
            \"sourceElectOther\": \"\",
            \"cookingFac\": \"\",
            \"cookingFacType\": \"\",
            \"cookingFacOther\": \"\",
            \"cultivatedLand\": [],
            \"agriculturalProducts\": [],
            \"agriculturalMachineries\": [],
            \"animalsRaised\": [],
            \"petAnimals\": [],
            \"fisheriesEntries\": [],
            \"boatEntries\": [],
            \"nipaEntries\": [],
            \"fishingGearEntries\": [],
            \"environmentSanitation\": {
              \"selectedWaterSource\": [],
              \"selectedWaterLocation\": \"\",
              \"isWaterAvailable\": null,
              \"selectedToiletFacility\": [],
              \"selectedToiletLocation\": \"\",
              \"toiletSharing\": \"\",
              \"selectedGarbageDisposal\": []
            },
            \"familyMembers\": [],
            \"lastPageData\": {
              \"foodSecurity\": {},
              \"socialProtection\": {
                \"I1\": [],
                \"I2\": []
              },
              \"peaceAndOrder\": {
                \"J1\": \"\",
                \"J2\": \"\",
                \"J3\": \"\",
                \"J4\": \"\"
              }
            },
            \"houseExtension\": \"\",
            \"houseExtensionOther\": \"\",
            \"wallThickness\": \"\",
            \"wallThicknessOther\": \"\",
            \"steelStandard\": \"\",
            \"steelStandardOther\": \"\",
            \"wallSupport\": \"\",
            \"wallSupportOther\": \"\",
            \"gableWallMaterial\": \"\",
            \"gableWallMaterialOther\": \"\",
            \"foundationMaterial\": \"\",
            \"foundationMaterialOther\": \"\",
            \"soilCondition\": \"\",
            \"soilConditionOther\": \"\",
            \"houseCondition\": \"\",
            \"houseConditionOther\": \"\",
            \"houseLocation\": \"\",
            \"houseLocationOther\": \"\",
            \"ecologicalSetting\": \"\",
            \"buildingPermit\": \"\",
            \"houseBuiltBefore1990\": \"\",
            \"houseAffectedByDisaster\": \"\",
            \"houseShape\": \"\"
          }`;

      const prompt = `Extract text from the image/document provided and map what you can map or translate according to this JSON structure: ${payloadStructureString}.` + 
                     "\nNote that some of the fields are multiple entries, so please return them as arrays." +
                     "\nFor sex of the person just put Male or Female only." +
                     "\nD6 and D7 are tables and can have multiple entries, the structure is the header of the table and the answers are the rows." +
                     "\nFor Family Members Demographic they will write full name. Split it in three if they are 3(i.e. Juan Dela Cruz firstName: Juan, middleName: Dela, lastName: Cruz). Questions F to G21, the labels are header column then it is by rows. like for F1 the headers are 1) 2) 3).. to 9) meaning these are family members. If they have 3 entries here, we need to append 3 rows in the familyMembers names are below, the question is on the left their corresponding answer is in number." +
                     "\nFor F12 to F21 are just continuation of F1 to F11, do not overwrite the previous answer in case but instead add what your extracted here in the model." +
                     "\nFor F22 to F24 are just continuation of F1-F21, do not overwrite the previous answer in case but instead add what your extracted here in the model." +
                     "\nG1 to G16 are continuation of Family Members but different category. These are the questions for their health and nutrition." +
                     "\nFor G17 to G21 answer/process this only if the family member is 0-23 months old." +
                     "\nFor C to C16 they are encircled items what we expect here is to map them in houseExtension, houseShape, etc. (see payload)" +
                     "\nH1 to H11 are true or false: Answer is on the right as check mark." +
                     "\nSome of the items are checkboxes, most of them are encircled get the number/value of the encircled item." +
                     "\nSome of the items are by column (labels are header, values are rows) specially in the images: 5th, 7th, 8th, 9th, 10th uploads." +
                     "\nPlease return only valid JSON following the structure provided strictly the structure just mapping the values.";

      // Extract just the base64 part (remove data:image/...;base64, prefix)
      const base64Image = images[currentIndex].split(',')[1];
      // Get the image MIME type (e.g., 'image/png')
      const mimeTypeMatch = images[currentIndex].match(/^data:(.*?);/);
      const mimeType = mimeTypeMatch ? mimeTypeMatch[1] : "image/png";
      
      const response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${OPENAI_API_KEY}`,
        },
        body: JSON.stringify({
          model: "gpt-4o", // or "gpt-4-vision-preview" if you have access
          messages: [
            {
              role: "system",
              content: "You are an admin of BIDANI (Barangay Integrated Development Approach for Nutrition Improvement) that extracts structured data of survey forms. Return only valid JSON."
            },
            {
              role: "user",
              content: [
                { type: "text", text: prompt },
                {
                  type: "image_url",
                  image_url: {
                    url: `data:${mimeType};base64,${base64Image}`
                  }
                }
              ]
            }
          ],
          max_tokens: 2048,
          response_format: { type: "json_object" }
        }),
      });

      const data = await response.json();

      let jsonResult = null;
      if (data.choices?.[0]?.message?.content) {
        try {
          jsonResult = JSON.parse(data.choices[0].message.content);
        } catch (err) {
          console.error("Failed to parse JSON:", err);
          jsonResult = { error: "Invalid JSON response", raw: data.choices[0].message.content };
        }
      }

      // Merge new AI response into finalPayload, but keep existing non-empty values
      if (jsonResult && typeof jsonResult === "object" && !jsonResult.error) {
        setFinalPayload(prev => deepMergeKeepExisting(prev, jsonResult));
      }

      const newResponses = [...responses];
      newResponses[currentIndex] = jsonResult || { error: "No valid response" };
      setResponses(newResponses);

      if (currentIndex < MAX_IMAGES - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    } catch (err) {
      console.error("API Error:", err);
      alert("Error sending image to OpenAI: " + err.message);
    } finally {
      setLoading(false);
      setBusyDialog(false);
    }
  };

  // Show a message box for backend submission result
  const [messageBox, setMessageBox] = useState({ open: false, message: "", success: false });

  const handleSubmitToBackend = async () => {
    setSubmitLoading(true);
    setSubmitResult(null);
    try {
      const payloadStructureString = `{
            \"householdType\": \"\",
            \"longitude\": null,
            \"latitude\": null,
            \"houseNo\": \"\",
            \"street\": \"\",
            \"purokSitio\": \"\",
            \"barangay\": \"\",
            \"cityMunicipality\": \"\",
            \"province\": \"\",
            \"municipality\": \"\",
            \"firstName\": \"\",
            \"middleName\": \"\",
            \"lastName\": \"\",
            \"suffix\": \"\",
            \"familyType\": \"\",
            \"headID\": \"\",
            \"status\": \"\",
            \"sex\": \"\",
            \"gender\": \"\",
            \"civilStatus\": \"\",
            \"numFamilyMembers\": null,
            \"yearMoved\": null,
            \"prevLocation\": \"\",
            \"mobileProvider\": [],
            \"contactNumber\": [],
            \"internetProvider\": \"\",
            \"internetProviderOther\": \"\",
            \"householdAppliances\": {},
            \"ictDevices\": {},
            \"vehicles\": {},
            \"householdAssets\": [],
            \"lotOwnership\": \"\",
            \"lotOwnershipType\": \"\",
            \"houseOwnership\": \"\",
            \"houseOwnershipType\": \"\",
            \"lotOwnershipOther\": \"\",
            \"houseOwnershipOther\": \"\",
            \"houseStructure\": \"\",
            \"houseStructureType\": \"\",
            \"houseStructureOther\": \"\",
            \"externalHouseWall\": \"\",
            \"externalHouseWallType\": \"\",
            \"externalHouseWallOther\": \"\",
            \"roofingMat\": \"\",
            \"roofingMatType\": \"\",
            \"roofingMatOther\": \"\",
            \"floorMat\": \"\",
            \"floorMatType\": \"\",
            \"floorMatOther\": \"\",
            \"sourceElect\": \"\",
            \"sourceElectType\": \"\",
            \"sourceElectOther\": \"\",
            \"cookingFac\": \"\",
            \"cookingFacType\": \"\",
            \"cookingFacOther\": \"\",
            \"cultivatedLand\": [],
            \"agriculturalProducts\": [],
            \"agriculturalMachineries\": [],
            \"animalsRaised\": [],
            \"petAnimals\": [],
            \"fisheriesEntries\": [],
            \"boatEntries\": [],
            \"nipaEntries\": [],
            \"fishingGearEntries\": [],
            \"environmentSanitation\": {
              \"selectedWaterSource\": [],
              \"selectedWaterLocation\": \"\",
              \"isWaterAvailable\": null,
              \"selectedToiletFacility\": [],
              \"selectedToiletLocation\": \"\",
              \"toiletSharing\": \"\",
              \"selectedGarbageDisposal\": []
            },
            \"familyMembers\": [],
            \"lastPageData\": {
              \"foodSecurity\": {},
              \"socialProtection\": {
                \"I1\": [],
                \"I2\": []
              },
              \"peaceAndOrder\": {
                \"J1\": \"\",
                \"J2\": \"\",
                \"J3\": \"\",
                \"J4\": \"\"
              }
            },
            \"houseExtension\": \"\",
            \"houseExtensionOther\": \"\",
            \"wallThickness\": \"\",
            \"wallThicknessOther\": \"\",
            \"steelStandard\": \"\",
            \"steelStandardOther\": \"\",
            \"wallSupport\": \"\",
            \"wallSupportOther\": \"\",
            \"gableWallMaterial\": \"\",
            \"gableWallMaterialOther\": \"\",
            \"foundationMaterial\": \"\",
            \"foundationMaterialOther\": \"\",
            \"soilCondition\": \"\",
            \"soilConditionOther\": \"\",
            \"houseCondition\": \"\",
            \"houseConditionOther\": \"\",
            \"houseLocation\": \"\",
            \"houseLocationOther\": \"\",
            \"ecologicalSetting\": \"\",
            \"buildingPermit\": \"\",
            \"houseBuiltBefore1990\": \"\",
            \"houseAffectedByDisaster\": \"\",
            \"houseShape\": \"\"
          }`;
      
    console.log("Final payload before sending to OpenAI:", finalPayload);
    console.log("Payload structure string:", payloadStructureString);
    // Parse the payload structure string to an object
    let payloadTemplateObj = {};
    try {
      payloadTemplateObj = JSON.parse(payloadStructureString.replace(/\\"/g, '"'));
    } catch (e) {
      payloadTemplateObj = {};
    }

    // Merge finalPayload into the template so all expected fields are present and filled
    const mergedPayload = deepMergeKeepExisting(payloadTemplateObj, finalPayload);

    const prompt = `Please check thoroughly and map the extracted payload: ${JSON.stringify(finalPayload)} to our expected payload: ${payloadStructureString}.` +
      "\nReturn it as the same expected payload and follow the expected json payload structure strictly. You can return it as a json string.";
    const responsePayload = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${OPENAI_API_KEY}`,
        },
        body: JSON.stringify({
          model: "gpt-4o", // or "gpt-4-vision-preview" if you have access
          messages: [
            {
              role: "system",
              content: "You are an assistant that will help extract survey forms images to json format. Return as expected payload as json string."
            },
            {
              role: "user",
              content: [
                { type: "text", text: prompt }
              ]
            }
          ],
          max_tokens: 2048,
          response_format: { type: "json_object" }
        }),
      });
      const data = await responsePayload.json();
      let jsonResult = null;
      if (data.choices?.[0]?.message?.content) {
        try {
          let content = data.choices[0].message.content;
          if (typeof content === "string") {
            try {
              jsonResult = JSON.parse(content);
            } catch {
              const match = content.match(/\{[\s\S]*\}/);
              if (match) {
                jsonResult = JSON.parse(match[0]);
              } else {
                jsonResult = { error: "Invalid JSON response", raw: content };
              }
            }
          } else {
            jsonResult = content;
          }
        } catch (err) {
          console.error("Failed to parse JSON:", err);
          jsonResult = { error: "Invalid JSON response", raw: data.choices[0].message.content };
        }
      }
      // Merge OpenAI result into the template as well, to ensure all fields are present and filled
      const finalMergedPayload = deepMergeKeepExisting(payloadTemplateObj, jsonResult || mergedPayload);


      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/upload/mass-upload`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_API_KEY,
          },
          body: JSON.stringify(finalMergedPayload),
        }
      );
      const result = await response.json();
      if (!response.ok) {
        setMessageBox({ open: true, message: result.message || "Submission failed.", success: false });
        setSubmitResult({ success: false, error: result.message });
      } else {
        setMessageBox({ open: true, message: "Submission successful!", success: true });
        setSubmitResult({ success: true, data: result });
      }
    } catch (err) {
      setMessageBox({ open: true, message: err.message, success: false });
      setSubmitResult({ success: false, error: err.message });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleNext = () => {
    if (currentIndex < MAX_IMAGES - 1) setCurrentIndex(currentIndex + 1);
  };

  return (
    <div style={{ maxWidth: "600px", margin: "0 auto", textAlign: "center", padding: "1rem" }}>
      <h1>Capture/Upload Survey Images</h1>
      <p>Step {currentIndex + 1} of {MAX_IMAGES}: Upload image {currentIndex + 1} of your survey form.</p>
      <input
        type="file"
        accept="image/*"
        capture="camera"
        onChange={handleCapture}
        style={{ marginBottom: "1rem" }}
        disabled={loading}
      />
      {images[currentIndex] && (
        <div style={{ marginBottom: "1rem" }}>
          <img src={images[currentIndex]} alt={`Captured ${currentIndex + 1}`} style={{ maxWidth: "100%", height: "auto" }} />
        </div>
      )}
      <div style={{ marginBottom: "1rem" }}>
        <button onClick={handlePrev} disabled={currentIndex === 0 || loading} style={{ marginRight: "0.5rem" }}>Prev</button>
        <button onClick={handleNext} disabled={currentIndex === MAX_IMAGES - 1 || loading}>Next</button>
      </div>
      <button
        onClick={handleSendToOpenAI}
        style={{ padding: "0.5rem 1rem", cursor: "pointer", marginBottom: "1rem" }}
        disabled={loading || !images[currentIndex]}
      >
        {loading ? "Processing..." : "Send Image to OpenAI"}
      </button>
      {responses[currentIndex] && (
        <div style={{ margin: "1rem 0", textAlign: "left", background: "#f7f7f7", padding: "1rem", borderRadius: "8px" }}>
          <strong>OpenAI Response (JSON):</strong>
          <pre style={{ fontSize: "0.9em", overflowX: "auto" }}>
            {JSON.stringify(responses[currentIndex], null, 2)}
          </pre>
        </div>
      )}
      <div style={{ marginTop: "2rem" }}>
        <h3>Progress</h3>
        <div style={{ display: "flex", justifyContent: "center", gap: "4px" }}>
          {images.map((img, idx) => (
            <div key={idx} style={{
              width: 24, height: 24, borderRadius: "50%",
              background: img ? "#4caf50" : "#ccc",
              border: idx === currentIndex ? "2px solid #1976d2" : "1px solid #aaa",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontWeight: "bold"
            }}>{idx + 1}</div>
          ))}
        </div>
      </div>
      {/* Show the current merged payload */}
      <div style={{ marginTop: "2rem", background: "#e3f2fd", padding: "1rem", borderRadius: "8px" }}>
        <h3>Current Merged Payload</h3>
        <pre style={{ fontSize: "0.9em", overflowX: "auto" }}>
          {JSON.stringify(finalPayload, null, 2)}
        </pre>
      </div>
      {/* Show Proceed button always, not just after last image */}
      <div style={{ marginTop: "2rem" }}>
        <button
          onClick={handleSubmitToBackend}
          style={{ padding: "0.75rem 2rem", fontSize: "1.1em", background: "#1976d2", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer" }}
          disabled={submitLoading}
        >
          {submitLoading ? "Submitting..." : "Proceed in Creating"}
        </button>
        {submitResult && (
          <div style={{ marginTop: "1rem", color: submitResult.success ? "green" : "red" }}>
            {submitResult.success
              ? "Submission successful!"
              : `Submission failed: ${submitResult.error}`}
            {submitResult.data && (
              <pre style={{ fontSize: "0.9em", overflowX: "auto" }}>
                {JSON.stringify(submitResult.data, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
      {/* Busy dialog/modal */}
      {busyDialog && (
        <div style={{
          position: "fixed",
          top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <div style={{
            background: "#fff",
            padding: "2rem 3rem",
            borderRadius: "12px",
            boxShadow: "0 2px 16px rgba(0,0,0,0.2)",
            textAlign: "center"
          }}>
            <div style={{ marginBottom: "1rem" }}>
              <span style={{ fontSize: "2rem", display: "block" }}>⏳</span>
            </div>
            <div style={{ fontSize: "1.2rem" }}>Processing image... please wait...</div>
          </div>
        </div>
      )}
      {/* Message box for backend result */}
      {messageBox.open && (
        <div style={{
          position: "fixed",
          top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.3)",
          zIndex: 10000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <div style={{
            background: "#fff",
            padding: "2rem 3rem",
            borderRadius: "12px",
            boxShadow: "0 2px 16px rgba(0,0,0,0.2)",
            textAlign: "center",
            minWidth: "300px"
          }}>
            <div style={{ marginBottom: "1rem", color: messageBox.success ? "green" : "red" }}>
              <strong>{messageBox.success ? "Success" : "Error"}</strong>
            </div>
            <div style={{ marginBottom: "1rem" }}>{messageBox.message}</div>
            <button
              style={{ padding: "0.5rem 1.5rem", borderRadius: "6px", border: "none", background: "#1976d2", color: "#fff", cursor: "pointer" }}
              onClick={() => setMessageBox({ ...messageBox, open: false })}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaptureUploadImage;
